import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Input,
  Badge,
  DataTable,
  DrawerBody,
  DrawerFooter,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useToast,
  type DataTableColumn,
} from "@payflow/ui";
import { api } from "../../services/api";

export interface TenantEmpresa {
  tipoPessoa: "pf" | "pj";
  razaoSocial: string;
  nomeFantasia?: string;
  cnpj: string;
  inscricaoEstadual?: string;
  inscricaoMunicipal?: string;
  endereco?: {
    logradouro?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
    cep?: string;
  };
  telefoneEmpresa?: string;
  emailEmpresa?: string;
  pixEmpresa?: string;
  pixTipoEmpresa?: "cnpj" | "email" | "telefone" | "aleatoria";
}

interface Row extends TenantEmpresa {
  id: string;
}

export const EMPTY_COMPANY: TenantEmpresa = {
  tipoPessoa: "pj",
  razaoSocial: "",
  cnpj: "",
  endereco: {},
};

function formatDoc(doc: string, tipo: "pf" | "pj"): string {
  const d = (doc || "").replace(/\D/g, "");
  if (tipo === "pf" && d.length === 11) {
    return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
  }
  if (tipo === "pj" && d.length === 14) {
    return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
  }
  return doc || "-";
}

// Fetcher compartilhado — usado pela Page (lista) e pela DetailPage (edicao).
export async function fetchCompany(): Promise<TenantEmpresa | null> {
  const res = await api.get<TenantEmpresa | null>("/settings/empresa");
  if (!res.data) return null;
  return {
    ...EMPTY_COMPANY,
    ...res.data,
    endereco: { ...EMPTY_COMPANY.endereco, ...(res.data.endereco || {}) },
  };
}

export async function saveCompany(payload: Partial<TenantEmpresa>): Promise<void> {
  await api.put("/settings/empresa", payload);
}

export function SettingsCompanyPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState<TenantEmpresa | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchCompany();
      if (result) setData(result);
    } catch {
      toast({ variant: "error", title: t("settings.company.load_error") });
    } finally {
      setLoading(false);
    }
  }, [t, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const openEdit = () => navigate("/dashboard/configuracoes/empresa/editar");

  const isPJ = data?.tipoPessoa === "pj";
  const title = isPJ ? t("settings.company.title_pj") : t("settings.company.title_pf");
  const subtitle = isPJ ? t("settings.company.subtitle_pj") : t("settings.company.subtitle_pf");

  const columns: DataTableColumn<Row>[] = [
    {
      key: "razaoSocial",
      header: isPJ ? t("settings.company.razao_social") : t("settings.company.nome_completo"),
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 dark:bg-primary-900/30">
            <svg className="h-4 w-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium text-foreground">{row.razaoSocial || "-"}</p>
            {row.nomeFantasia && (
              <p className="truncate text-xs text-muted-foreground">{row.nomeFantasia}</p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: "cnpj",
      header: isPJ ? t("settings.company.cnpj") : t("settings.company.cpf"),
      render: (row) => (
        <span className="font-mono text-sm text-muted-foreground">
          {formatDoc(row.cnpj, row.tipoPessoa)}
        </span>
      ),
    },
    {
      key: "email",
      header: t("settings.company.email"),
      render: (row) => (
        <span className="text-sm text-muted-foreground">{row.emailEmpresa || "-"}</span>
      ),
    },
    {
      key: "cidade",
      header: t("settings.company.cidade"),
      render: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.endereco?.cidade
            ? `${row.endereco.cidade}${row.endereco.estado ? "/" + row.endereco.estado : ""}`
            : "-"}
        </span>
      ),
    },
    {
      key: "type",
      header: "",
      render: (row) => (
        <Badge variant="outline" size="sm">
          {row.tipoPessoa.toUpperCase()}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: "",
      className: "w-16 text-right",
      render: () => (
        <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={openEdit}
            title={t("common.edit")}
          >
            <EditIcon />
          </Button>
        </div>
      ),
    },
  ];

  const rows: Row[] = data ? [{ ...data, id: "self" }] : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{title}</h1>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
        <Button onClick={openEdit} disabled={!data}>
          <EditIcon />
          <span className="ml-2">{t("common.edit")}</span>
        </Button>
      </div>

      {/* DataTable */}
      <DataTable
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage={t("settings.company.load_error")}
        onRowClick={openEdit}
      />
    </div>
  );
}

// ============ Company Form (Drawer ou Page) ============

export interface CompanyFormProps {
  initial: TenantEmpresa;
  saving: boolean;
  onSave: (payload: Partial<TenantEmpresa>) => void;
  /** "drawer" (legacy) ou "page" (usado em SettingsCompanyDetailPage). */
  variant?: "drawer" | "page";
}

export function CompanyForm({ initial, saving, onSave, variant = "drawer" }: CompanyFormProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("cadastro");
  const [form, setForm] = useState<TenantEmpresa>({
    ...initial,
    endereco: { ...(initial.endereco || {}) },
  });

  const isPJ = form.tipoPessoa === "pj";
  const labelNome = isPJ ? t("settings.company.razao_social") : t("settings.company.nome_completo");
  const labelDoc = isPJ ? t("settings.company.cnpj") : t("settings.company.cpf");

  const update = <K extends keyof TenantEmpresa>(key: K, value: TenantEmpresa[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };
  const updateEndereco = (key: keyof NonNullable<TenantEmpresa["endereco"]>, value: string) => {
    setForm((prev) => ({ ...prev, endereco: { ...(prev.endereco || {}), [key]: value } }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      razaoSocial: form.razaoSocial,
      nomeFantasia: isPJ ? form.nomeFantasia : undefined,
      cnpj: form.cnpj,
      inscricaoEstadual: isPJ ? form.inscricaoEstadual : undefined,
      inscricaoMunicipal: isPJ ? form.inscricaoMunicipal : undefined,
      endereco: form.endereco,
      telefoneEmpresa: form.telefoneEmpresa,
      emailEmpresa: form.emailEmpresa,
      pixEmpresa: form.pixEmpresa,
      pixTipoEmpresa: form.pixTipoEmpresa,
    });
  };

  const isPage = variant === "page";
  const Body = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="min-h-0 flex-1 overflow-y-auto px-1 py-4">{children}</div>
      )
    : DrawerBody;
  const Footer = isPage
    ? ({ children }: { children: ReactNode }) => (
        <div className="flex justify-end gap-2 border-t border-border pt-4">{children}</div>
      )
    : DrawerFooter;

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col">
      <Body>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full">
            <TabsTrigger value="cadastro" className="flex-1">
              {isPJ ? t("settings.company.section_pj") : t("settings.company.section_pf")}
            </TabsTrigger>
            <TabsTrigger value="endereco" className="flex-1">
              {t("settings.company.address")}
            </TabsTrigger>
            <TabsTrigger value="pix" className="flex-1">
              {t("settings.company.pix")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cadastro">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={labelNome}
                  value={form.razaoSocial}
                  onChange={(e) => update("razaoSocial", e.target.value)}
                  required
                />
                <Input
                  label={labelDoc}
                  value={form.cnpj}
                  onChange={(e) => update("cnpj", e.target.value)}
                  disabled
                />
              </div>

              {isPJ && (
                <div className="grid gap-4 sm:grid-cols-3">
                  <Input
                    label={t("settings.company.nome_fantasia")}
                    value={form.nomeFantasia || ""}
                    onChange={(e) => update("nomeFantasia", e.target.value)}
                  />
                  <Input
                    label={t("settings.company.ie")}
                    value={form.inscricaoEstadual || ""}
                    onChange={(e) => update("inscricaoEstadual", e.target.value)}
                  />
                  <Input
                    label={t("settings.company.im")}
                    value={form.inscricaoMunicipal || ""}
                    onChange={(e) => update("inscricaoMunicipal", e.target.value)}
                  />
                </div>
              )}

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label={t("settings.company.email")}
                  type="email"
                  value={form.emailEmpresa || ""}
                  onChange={(e) => update("emailEmpresa", e.target.value)}
                />
                <Input
                  label={t("settings.company.telefone")}
                  value={form.telefoneEmpresa || ""}
                  onChange={(e) => update("telefoneEmpresa", e.target.value)}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="endereco">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t("settings.company.cep")}
                  value={form.endereco?.cep || ""}
                  onChange={(e) => updateEndereco("cep", e.target.value)}
                />
                <div className="sm:col-span-2">
                  <Input
                    label={t("settings.company.logradouro")}
                    value={form.endereco?.logradouro || ""}
                    onChange={(e) => updateEndereco("logradouro", e.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <Input
                  label={t("settings.company.numero")}
                  value={form.endereco?.numero || ""}
                  onChange={(e) => updateEndereco("numero", e.target.value)}
                />
                <Input
                  label={t("settings.company.complemento")}
                  value={form.endereco?.complemento || ""}
                  onChange={(e) => updateEndereco("complemento", e.target.value)}
                />
                <Input
                  label={t("settings.company.bairro")}
                  value={form.endereco?.bairro || ""}
                  onChange={(e) => updateEndereco("bairro", e.target.value)}
                />
              </div>
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="sm:col-span-2">
                  <Input
                    label={t("settings.company.cidade")}
                    value={form.endereco?.cidade || ""}
                    onChange={(e) => updateEndereco("cidade", e.target.value)}
                  />
                </div>
                <Input
                  label={t("settings.company.estado")}
                  value={form.endereco?.estado || ""}
                  onChange={(e) =>
                    updateEndereco("estado", e.target.value.toUpperCase().slice(0, 2))
                  }
                  maxLength={2}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pix">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">
                  {t("settings.company.pix_type")}
                </label>
                <select
                  value={form.pixTipoEmpresa || ""}
                  onChange={(e) =>
                    update(
                      "pixTipoEmpresa",
                      (e.target.value || undefined) as TenantEmpresa["pixTipoEmpresa"]
                    )
                  }
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground"
                >
                  <option value="">{t("settings.company.pix_type_none")}</option>
                  <option value="cnpj">
                    {isPJ
                      ? t("settings.company.pix_type_cnpj")
                      : t("settings.company.pix_type_cpf")}
                  </option>
                  <option value="email">{t("settings.company.pix_type_email")}</option>
                  <option value="telefone">{t("settings.company.pix_type_phone")}</option>
                  <option value="aleatoria">{t("settings.company.pix_type_random")}</option>
                </select>
              </div>
              <Input
                label={t("settings.company.pix_key")}
                value={form.pixEmpresa || ""}
                onChange={(e) => update("pixEmpresa", e.target.value)}
              />
            </div>
          </TabsContent>
        </Tabs>
      </Body>

      <Footer>
        <Button type="submit" loading={saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      </Footer>
    </form>
  );
}

function EditIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  );
}
